import type { MobiLauncherConfig } from '../schema/index.js';
import type { Profile, ProfileSchedule } from '../schema/profile.js';

/**
 * Resolve the profile that should be active at `now` (device local time).
 *
 * Algorithm:
 *   1. If `scheduledProfiles` isn't entitled, the schedule list is ignored.
 *   2. Otherwise iterate `config.schedules` in array order; the first
 *      matching schedule wins (per SCHEMA.md).
 *   3. If nothing matches, fall through to `defaultProfileId`.
 *
 * "Matching" = `now`'s day-of-week is in `daysOfWeek` AND `now`'s time is
 * within the `[startTime, endTime)` window. A window where end < start
 * (e.g., 20:00 → 06:00) wraps midnight; this is the common night-shift case.
 *
 * Returns the resolved Profile. Throws if `defaultProfileId` doesn't resolve
 * — but that's a schema-level invariant, so in practice it can't happen
 * after parse.
 */
export function resolveActiveProfile(input: {
  config: MobiLauncherConfig;
  now: Date;
  scheduledProfilesEntitled: boolean;
}): Profile {
  const { config, now, scheduledProfilesEntitled } = input;

  if (scheduledProfilesEntitled) {
    for (const schedule of config.schedules) {
      if (matches(schedule, now)) {
        const profile = config.profiles.find((p) => p.id === schedule.profileId);
        if (profile) return profile;
      }
    }
  }

  const fallback = config.profiles.find((p) => p.id === config.defaultProfileId);
  if (!fallback) {
    throw new Error(
      `defaultProfileId '${config.defaultProfileId}' not found in profiles[] — schema invariant violated`,
    );
  }
  return fallback;
}

function matches(schedule: ProfileSchedule, now: Date): boolean {
  const minutes = now.getHours() * 60 + now.getMinutes();
  const start = parseHHmm(schedule.startTime);
  const end = parseHHmm(schedule.endTime);

  if (start === end) return false; // zero-duration window

  const today = now.getDay();
  const yesterday = (today + 6) % 7; // (today - 1) mod 7

  if (start < end) {
    // Same-day window: a listed day, inside [start, end).
    return schedule.daysOfWeek.includes(today) && minutes >= start && minutes < end;
  }
  // Wrap-around window (e.g., 20:00 → 06:00) spans midnight into the next day:
  //   - the evening portion [start, 24:00) belongs to the listed day itself;
  //   - the morning portion [00:00, end) is the TAIL of a shift that started
  //     the previous day, so it matches only when YESTERDAY is a listed day.
  // (The old code gated on today's day-of-week for both, so a Fri-night shift
  //  wrongly lit up Fri 03:00 and never reached Sat 03:00.)
  if (minutes >= start) return schedule.daysOfWeek.includes(today);
  if (minutes < end) return schedule.daysOfWeek.includes(yesterday);
  return false;
}

function parseHHmm(s: string): number {
  // Schema regex guarantees `HH:mm`; safe to split.
  const [h, m] = s.split(':');
  return Number(h) * 60 + Number(m);
}
